import { MEDIA } from "../mock";
import { useReveal } from "../hooks/useReveal";
import { ArrowUpRight, FileText, FolderArchive, CalendarClock } from "lucide-react";

export const PlatformShowcase = ({ onCtaClick }) => {
  const ref = useReveal();
  return (
    <section id="platform" data-testid="platform-showcase" className="relative py-28 lg:py-32 bg-[#0A1128]/40 border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left text */}
          <div ref={ref} className="reveal lg:col-span-5">
            <span className="overline">The portal</span>
            <h2 className="font-heading mt-4 text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]">
              Records, documents,
              <br />
              <span className="gold-text">schedules — unified.</span>
            </h2>
            <p className="mt-6 text-white/65 text-lg leading-relaxed">
              A role-scoped portal for every tier — from Controllers to Department Teams. Every
              Flight Record Log, every document, every crew warning — stored once, retrieved by the
              right person, attributed in the audit trail.
            </p>

            <div className="mt-10 space-y-6">
              {[
                { Icon: FileText, label: "Flight Record Logs maintained per aircraft and per crew" },
                { Icon: FolderArchive, label: "Centralised document storage with versioned record keeping" },
                { Icon: CalendarClock, label: "Schedules, crew warnings and role-scoped records — in one view" },
              ].map(({ Icon, label }, i) => (
                <div key={i} data-testid={`showcase-point-${i}`} className="flex items-start gap-4">
                  <div className="h-10 w-10 border border-[#C19A5D]/40 bg-[#121A33] flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4 text-[#C19A5D]" strokeWidth={1.5} />
                  </div>
                  <p className="text-white/80 font-body leading-relaxed pt-2">{label}</p>
                </div>
              ))}
            </div>

            <button
              data-testid="showcase-cta"
              onClick={onCtaClick}
              className="group mt-12 inline-flex items-center gap-2 text-[#C19A5D] hover:text-[#D4AF74] transition-colors font-semibold uppercase tracking-wider text-xs"
            >
              See the full portal
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
            </button>
          </div>

          {/* Right image */}
          <div className="lg:col-span-7">
            <div className="tracing-border">
              <div className="relative bg-[#050814] overflow-hidden">
                <img
                  src={MEDIA.showcase}
                  alt="OmniLink portal showcase"
                  data-testid="showcase-image"
                  className="w-full h-auto object-cover"
                />
                <span className="absolute top-3 left-3 h-3 w-3 border-t border-l border-[#C19A5D]" />
                <span className="absolute top-3 right-3 h-3 w-3 border-t border-r border-[#C19A5D]" />
                <span className="absolute bottom-3 left-3 h-3 w-3 border-b border-l border-[#C19A5D]" />
                <span className="absolute bottom-3 right-3 h-3 w-3 border-b border-r border-[#C19A5D]" />
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between text-xs text-white/40 uppercase tracking-[0.2em] font-medium">
              <span>OmniLink Operations Portal</span>
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 bg-[#C19A5D] pulse-dot rounded-full" />
                Preview
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
